<script lang="ts">
    import { createEventDispatcher } from 'svelte'
    import Fa from 'svelte-fa'
    import { faClone, faGripVertical, faTrash } from '@fortawesome/free-solid-svg-icons'
    import { getBlockDefinition } from '../blocks/block-registry'
    import type { PlacedBlock } from '../types/placed-block'

    export let block: PlacedBlock
    export let index: number
    export let isDragging = false

    const dispatch = createEventDispatcher()
    const widthOptions: (1 | 2 | 3)[] = [1, 2, 3]

    $: definition = getBlockDefinition(block.type)

    function setWidth(width: 1 | 2 | 3) {
        block.width = width
        dispatch('widthchange', { index })
    }

    function onDragStart(event: DragEvent) {
        event.dataTransfer?.setData('application/json', JSON.stringify({ source: 'canvas', index }))
        event.dataTransfer.effectAllowed = 'move'
        dispatch('dragstart', { index })
    }

    function onDragEnd() {
        dispatch('dragend')
    }
</script>

<div
    class="canvas-block"
    class:dragging={isDragging}
    role="listitem"
    draggable="true"
    on:dragstart={onDragStart}
    on:dragend={onDragEnd}
>
    <div class="block-toolbar">
        <span class="grip" title="Arraste para reordenar">
            <Fa icon={faGripVertical} />
        </span>
        {#if block.type !== 'label'}
            <input class="block-title" type="text" bind:value={block.label} placeholder={definition?.label ?? 'Bloco'} />
        {:else}
            <div class="spacer" />
        {/if}
        <div class="block-actions">
            <button type="button" title="Duplicar" on:click={() => dispatch('duplicate', { index })}>
                <Fa icon={faClone} />
            </button>
            <button type="button" title="Excluir" class="danger" on:click={() => dispatch('remove', { index })}>
                <Fa icon={faTrash} />
            </button>
        </div>
    </div>

    <div class="block-content-row">
        <div class="block-body">
            {#if definition}
                <svelte:component this={definition.component} bind:data={block.data} />
            {/if}
        </div>

        <div class="block-settings">
            {#if !definition?.fixedFullWidth}
                <div class="setting-group">
                    <span class="setting-label">Largura no documento</span>
                    <div class="width-group" role="group">
                        {#each widthOptions as w}
                            <button type="button" class="width-btn" class:active={(block.width ?? 3) === w} on:click={() => setWidth(w)}>
                                {w}/3
                            </button>
                        {/each}
                    </div>
                </div>
            {/if}
            {#if definition?.collectsAnswer}
                <label class="setting-check">
                    <input type="checkbox" bind:checked={block.required} />
                    <span class="setting-label">Obrigatório</span>
                </label>
                <label class="setting-check">
                    <input type="checkbox" bind:checked={block.readOnlyForPublic} />
                    <span class="setting-label">
                        Somente leitura
                        <span class="setting-hint">O destinatário do link não poderá editar</span>
                    </span>
                </label>
            {/if}
        </div>
    </div>
</div>

<style>
    .canvas-block {
        background: #fff;
        border: 1px solid #ecedf0;
        border-radius: 12px;
        padding: 16px 18px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
        transition: box-shadow 0.15s ease, border-color 0.15s ease;
        cursor: grab;
    }
    .canvas-block:hover {
        border-color: #dde1e8;
        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
    }
    .canvas-block.dragging {
        opacity: 0.4;
    }
    .block-toolbar {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 10px;
    }
    .grip {
        color: #ced4da;
        flex-shrink: 0;
    }
    .block-title {
        flex: 1;
        border: none;
        border-bottom: 1px solid transparent;
        background: transparent;
        font-size: 0.95rem;
        font-weight: 500;
        color: #212529;
        padding: 4px 2px;
    }
    .block-title:hover {
        border-bottom-color: #dee2e6;
    }
    .block-title:focus {
        outline: none;
        border-bottom-color: var(--cs2-accent, #4263eb);
    }
    .block-actions {
        display: flex;
        align-items: center;
        gap: 4px;
        opacity: 0;
        transition: opacity 0.15s ease;
    }
    .canvas-block:hover .block-actions {
        opacity: 1;
    }
    .block-actions button {
        border: none;
        background: transparent;
        color: #868e96;
        padding: 6px;
        border-radius: 6px;
        cursor: pointer;
        font-size: 0.85rem;
    }
    .block-actions button:hover {
        background: #f1f3f5;
        color: #495057;
    }
    .block-actions button.danger:hover {
        background: #fff0f0;
        color: #fa5252;
    }
    .block-content-row {
        /* Configurações sempre abaixo do conteúdo (inclusive no desktop): o painel lateral
           espremia o conteúdo — e chegava a sobrepor — em blocos estreitos (largura 1/3). */
        display: flex;
        flex-direction: column;
        gap: 12px;
        cursor: default;
    }
    .block-body {
        min-width: 0;
        padding-left: 26px;
    }
    .block-settings {
        /* Ocupando a linha inteira, os itens (largura, obrigatório, somente leitura) ficam lado a
           lado em vez de empilhados. */
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        /* flex-start, não center: as opções têm alturas diferentes ("Largura no documento" tem os
           botões abaixo do rótulo, "Somente leitura" tem a linha de ajuda), e centralizar empurrava
           "Obrigatório" — a única sem segunda linha — pra baixo em relação às outras. */
        align-items: flex-start;
        gap: 10px 18px;
        padding: 10px 10px 0 26px;
        border-top: 1px solid #f1f3f5;
        cursor: default;
    }
    .setting-group {
        display: flex;
        flex-direction: column;
        gap: 5px;
    }
    .width-group {
        display: flex;
        gap: 4px;
    }
    .width-btn {
        flex: 1;
        border: 1px solid #dee2e6;
        background: #fff;
        color: #495057;
        border-radius: 6px;
        padding: 3px 6px;
        font-size: 0.74rem;
        cursor: pointer;
    }
    .width-btn:hover {
        border-color: var(--cs2-accent, #4263eb);
    }
    .width-btn.active {
        background: var(--cs2-accent, #4263eb);
        border-color: var(--cs2-accent, #4263eb);
        color: #fff;
    }
    .setting-check {
        display: flex;
        align-items: flex-start;
        gap: 7px;
        cursor: pointer;
    }
    .setting-check input[type='checkbox'] {
        margin-top: 2px;
        flex-shrink: 0;
        width: 14px;
        height: 14px;
        accent-color: var(--cs2-accent, #4263eb);
        cursor: pointer;
    }
    .setting-label {
        font-size: 0.78rem;
        color: #495057;
        display: flex;
        flex-direction: column;
        line-height: 1.35;
        gap: 2px;
    }
    .setting-label,
    .setting-hint {
        white-space: nowrap;
    }
    .setting-hint {
        font-size: 0.68rem;
        color: #adb5bd;
        font-style: italic;
        line-height: 1.3;
    }
</style>
